import type React from 'react';

import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { injectStyle } from 'react-toastify/dist/inject-style';
import styled from 'styled-components';

import AskElevate from '@/components/AskElevate/AskElevate';
import AdminNavbar from '@/components/Navbar/AdminNavbar';
import Panel from '@/components/Panel';
import SidebarNav from '@/components/SidebarNav';
import TaggedWordTooltip from '@/components/TaggedWordTooltip/TaggedWordTooltip';
import { GlobalContextProvider } from '@/context/GlobalContext';
import { useSession } from '@/hooks/useAuth';
import { useVersionCheck } from '@/hooks/useVersionCheck';
import { NotFound, ParentMap, Privacy, ResetPassword, SetPassword, SignIn } from '@/Pages/index';
import ParentAlignment from '@/Pages/ParentAlignment/ParentAlignment';

import { ErrorBoundary } from './ErrorBoundary';

const StyledDashboard = styled.div`
	position: relative;
	display: grid;
	grid-template-areas:
		'sidebar nav'
		'sidebar panel';
	grid-template-columns: auto 1fr;
	grid-template-rows: auto 1fr;
	height: 100vh;
	min-height: 0;

	> *:nth-child(2),
	> *:nth-child(3) {
		min-height: 0;
	}
`;
interface PrivateRouteProps {
	children: React.ReactElement;
}

const PrivateRoute = ({ children }: PrivateRouteProps): React.ReactElement | null => {
	const user = useSession();
	const location = useLocation();

	// Still loading auth state - don't redirect yet
	if (user === undefined) {
		return null;
	}

	return user ? children : <Navigate to="/login" state={{ from: location.pathname }} />;
};

const Admin = () => {
	useVersionCheck();

	return (
		<StyledDashboard>
			<AdminNavbar />
			<SidebarNav />
			<Panel />
		</StyledDashboard>
	);
};

function App() {
	injectStyle();

	return (
		<ErrorBoundary>
			<GlobalContextProvider>
				<BrowserRouter>
					<Routes>
						<Route path="/login" element={<SignIn />} />
						<Route path="/privacy-policy" element={<Privacy />} />
						<Route path="/" element={<ParentAlignment />} />
						<Route path="/reset-password" element={<ResetPassword />} />
						<Route path="/set-password" element={<SetPassword />} />
						<Route path="/course-map" element={<ParentMap />} />
						<Route
							path="/dashboard/*"
							element={
								<PrivateRoute>
									<Admin />
								</PrivateRoute>
							}
						/>
						<Route path="*" element={<NotFound />} />
					</Routes>
					<AskElevate />
				</BrowserRouter>
				<TaggedWordTooltip />
			</GlobalContextProvider>
		</ErrorBoundary>
	);
}

export default App;
